// Public sign-up form shown from the spectator view's "Register" button. Runs in
// two steps: (1) the three spec questions — full name, date of birth and mobile
// number (Cambodian by default) — validated locally with the shared domain
// validator and submitted to the unauthenticated public endpoint, which sends a
// one-time passcode to the mobile; (2) entering that passcode to confirm the
// number and complete the sign-up. Server-side field errors are the source of
// truth and are merged in.

import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  OTP_CODE_LENGTH,
  validateRegistration,
  type RegistrationErrors,
} from '../../domain';
import {
  submitRegistration,
  verifyOtp,
  resendOtp,
  RegistrationSubmitError,
  OtpError,
  type OtpChallengeIssued,
} from '../../data/publicClient';
import { useTranslation } from '../../state/I18nContext';

interface RegistrationDialogProps {
  open: boolean;
  tournamentId: string;
  tournamentName: string;
  onClose: () => void;
}

type Step = 'form' | 'otp' | 'done';

const EMPTY = { fullName: '', dateOfBirth: '', mobile: '' };

export function RegistrationDialog({
  open,
  tournamentId,
  tournamentName,
  onClose,
}: RegistrationDialogProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('form');
  const [fields, setFields] = useState(EMPTY);
  const [errors, setErrors] = useState<RegistrationErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // OTP step state.
  const [challenge, setChallenge] = useState<OtpChallengeIssued | null>(null);
  const [code, setCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resentNote, setResentNote] = useState<string | null>(null);

  const busy = submitting || verifying || resending;

  const set = (key: keyof typeof EMPTY) => (value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    // Clear a field's error as the user edits it.
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const reset = () => {
    setStep('form');
    setFields(EMPTY);
    setErrors({});
    setSubmitError(null);
    setSubmitting(false);
    setChallenge(null);
    setCode('');
    setOtpError(null);
    setVerifying(false);
    setResending(false);
    setResentNote(null);
  };

  const close = () => {
    if (busy) return;
    onClose();
    // Reset after the close transition so the form does not flash empty.
    setTimeout(reset, 250);
  };

  const submit = async () => {
    if (busy) return;
    setSubmitError(null);
    const local = validateRegistration(fields);
    if (!local.valid || !local.value) {
      setErrors(local.errors);
      return;
    }
    setSubmitting(true);
    try {
      const issued = await submitRegistration(tournamentId, local.value);
      setChallenge(issued);
      setStep('otp');
    } catch (e: unknown) {
      if (e instanceof RegistrationSubmitError) {
        setErrors(e.fields);
        setSubmitError(e.message);
      } else {
        setSubmitError(
          e instanceof Error ? e.message : t('registration.submitError'),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const verify = async () => {
    if (busy || !challenge || code.length !== OTP_CODE_LENGTH) return;
    setOtpError(null);
    setResentNote(null);
    setVerifying(true);
    try {
      await verifyOtp(tournamentId, challenge.registrationId, code);
      setStep('done');
    } catch (e: unknown) {
      // An already-verified sign-up (e.g. a double submit) is a success.
      if (e instanceof OtpError && e.code === 'ALREADY_VERIFIED') {
        setStep('done');
        return;
      }
      setOtpError(
        e instanceof Error ? e.message : t('otp.verifyError'),
      );
      setCode('');
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    if (busy || !challenge) return;
    setOtpError(null);
    setResentNote(null);
    setResending(true);
    try {
      const issued = await resendOtp(tournamentId, challenge.registrationId);
      setChallenge(issued);
      setCode('');
      setResentNote(t('otp.resent'));
    } catch (e: unknown) {
      setOtpError(e instanceof Error ? e.message : t('otp.resendError'));
    } finally {
      setResending(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      {step === 'done' ? (
        <>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('done.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('done.body', { name: tournamentName })}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={close} variant="contained" fullWidth>
              {t('actions.done')}
            </Button>
          </DialogActions>
        </>
      ) : step === 'otp' ? (
        <>
          <DialogTitle sx={{ fontWeight: 700 }}>{t('otp.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('otp.intro', {
                length: OTP_CODE_LENGTH,
                mobile: challenge?.maskedMobile ?? '',
              })}
            </DialogContentText>
            <TextField
              label={t('otp.codeLabel')}
              fullWidth
              autoFocus
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, '').slice(0, OTP_CODE_LENGTH))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') void verify();
              }}
              error={Boolean(otpError)}
              helperText={otpError ?? ' '}
              disabled={verifying}
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  autoComplete: 'one-time-code',
                  maxLength: OTP_CODE_LENGTH,
                  style: { letterSpacing: '0.4em', fontVariantNumeric: 'tabular-nums' },
                },
              }}
            />
            {resentNote && (
              <Alert severity="success" sx={{ mt: 1 }}>
                {resentNote}
              </Alert>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('otp.didntGet')}
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => void resend()}
                disabled={busy}
              >
                {resending ? t('otp.sending') : t('otp.resend')}
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={close} disabled={busy}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => void verify()}
              disabled={busy || code.length !== OTP_CODE_LENGTH}
              startIcon={
                verifying ? <CircularProgress size={18} color="inherit" /> : undefined
              }
            >
              {verifying ? t('otp.verifying') : t('otp.verify')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ fontWeight: 700 }}>{t('registration.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('registration.intro', { name: tournamentName })}
            </DialogContentText>
            <Stack spacing={2}>
              <TextField
                label={t('registration.fullName')}
                fullWidth
                autoFocus
                value={fields.fullName}
                onChange={(e) => set('fullName')(e.target.value)}
                error={Boolean(errors.fullName)}
                helperText={errors.fullName ?? ' '}
                disabled={submitting}
              />
              <TextField
                label={t('registration.dateOfBirth')}
                type="date"
                fullWidth
                value={fields.dateOfBirth}
                onChange={(e) => set('dateOfBirth')(e.target.value)}
                error={Boolean(errors.dateOfBirth)}
                helperText={errors.dateOfBirth ?? ' '}
                disabled={submitting}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label={t('registration.mobile')}
                type="tel"
                fullWidth
                placeholder={t('registration.mobilePlaceholder')}
                value={fields.mobile}
                onChange={(e) => set('mobile')(e.target.value)}
                error={Boolean(errors.mobile)}
                helperText={errors.mobile ?? t('registration.mobileHelp')}
                disabled={submitting}
              />
            </Stack>
            {submitError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {submitError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={close} disabled={submitting}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => void submit()}
              disabled={submitting}
              startIcon={
                submitting ? <CircularProgress size={18} color="inherit" /> : undefined
              }
            >
              {submitting ? t('registration.sendingCode') : t('registration.continue')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
